import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Add, Refresh, GitHub, Lock, Warning } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import {
  useGetReposQuery,
  useGetAvailableReposQuery,
  useGetScanQuery,
  useConnectRepoMutation,
  useTriggerScanMutation,
  useUpdateRepoMutation,
  AvailableRepository,
} from '../../store/api/githubApi';
import { RepositoryCard } from '../../components/repositories/RepositoryCard';
import { RepositoryCardSkeleton } from '../../components/repositories/RepositoryCardSkeleton';
import { useNotification } from '../../hooks/useNotification';
import { NotificationSnackbar } from '../../components/common/NotificationSnackbar';
import { useWebSocket, WsEvent } from '../../hooks/useWebSocket';
import { SideDrawer } from '../../components/common/SideDrawer';

interface ScanState {
  status: string;
  scanId: number | null;
  message?: string; // latest message from backend WS (not persisted)
}

const STORAGE_KEY = 'kg_scan_states';

// Only persist status + scanId — never the message (it goes stale after refresh)
function readStorage(): Record<number, ScanState> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<number, ScanState>;
    // Strip messages so cycling fallback runs immediately on restore
    Object.values(parsed).forEach((s) => { delete s.message; });
    return parsed;
  } catch {
    return {};
  }
}

function writeStorage(states: Record<number, ScanState>) {
  try {
    // Persist only the fields we need; drop message
    const slim = Object.fromEntries(
      Object.entries(states).map(([k, s]) => [k, { status: s.status, scanId: s.scanId }])
    );
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(slim));
  } catch { /* ignore */ }
}

// ── Per-scan status sync ──────────────────────────────────────────────────────
// Renders nothing — polls the scan API every 5 s and pushes status back up.
// One instance per active scan with a known scanId.
interface ScanSyncProps {
  scanId: number;
  repoId: number;
  onUpdate: (repoId: number, status: string) => void;
}

const ScanStatusSync: React.FC<ScanSyncProps> = ({ scanId, repoId, onUpdate }) => {
  const { data: scan } = useGetScanQuery(scanId, { pollingInterval: 5000 });
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  useEffect(() => {
    if (scan?.status) onUpdateRef.current(repoId, scan.status);
  }, [scan?.status, repoId]);

  return null;
};

export const RepositoriesPage: React.FC = () => {
  const { user, accessToken } = useSelector((state: RootState) => state.auth);
  const { notification, hideNotification, showSuccess, showError } = useNotification();

  // Restored from sessionStorage so state survives page navigation
  const [scanStates, setScanStates] = useState<Record<number, ScanState>>(readStorage);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Persist scan states across navigation
  useEffect(() => {
    writeStorage(scanStates);
  }, [scanStates]);

  const hasActiveScans = Object.values(scanStates).some(
    (s) => !['complete', 'failed'].includes(s.status)
  );

  // Poll repos while a scan is running so the score appears as soon as it's ready
  const { data: repos, isLoading, error, refetch } = useGetReposQuery(undefined, {
    pollingInterval: hasActiveScans ? 5000 : 0,
  });

  // When repos update, clear any scan states where the scan is now reflected in the API
  useEffect(() => {
    if (!repos) return;
    setScanStates((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const repo of repos) {
        const state = next[repo.id];
        if (!state) continue;
        // If the repo now has a score for this scan (or the scan_id matches and score is set), clear it
        if (
          state.scanId &&
          repo.latest_scan_id === state.scanId &&
          repo.latest_debt_score !== null
        ) {
          delete next[repo.id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [repos]);

  const { data: availableRepos, isLoading: availableLoading } = useGetAvailableReposQuery(
    undefined,
    { skip: !addDialogOpen }
  );

  const [connectRepo, { isLoading: connecting }] = useConnectRepoMutation();
  const [triggerScan] = useTriggerScanMutation();
  const [updateRepo, { isLoading: togglingPrGate }] = useUpdateRepoMutation();

  const UNSUPPORTED_LANGUAGES = new Set(['C', 'C++', 'C#']);

  const connectedNames = new Set((repos ?? []).map((r) => r.full_name));

  // Called by ScanStatusSync when a polled scan returns a new status
  const handleScanStatusUpdate = useCallback((repoId: number, status: string) => {
    setScanStates((prev) => {
      if (!prev[repoId]) return prev;
      if (prev[repoId].status === status) return prev;
      // If the scan is done, clear it and trigger a repo refetch to show the score
      if (['complete', 'failed'].includes(status)) {
        const next = { ...prev };
        delete next[repoId];
        refetchRef.current();
        setTimeout(() => refetchRef.current(), 3000);
        return next;
      }
      return { ...prev, [repoId]: { ...prev[repoId], status } };
    });
  }, []);

  // Keep a ref so the WS callback always sees the latest refetch
  const refetchRef = useRef(refetch);
  useEffect(() => { refetchRef.current = refetch; }, [refetch]);

  const handleWsEvent = useCallback(
    (event: WsEvent) => {
      if (event.event === 'scan_status') {
        setScanStates((prev) => {
          const entry = Object.entries(prev).find(([, s]) => s.scanId === event.scan_id);
          if (!entry) return prev;
          return {
            ...prev,
            [entry[0]]: {
              ...prev[Number(entry[0])],
              status: event.status,
              message: event.message || undefined,
            },
          };
        });
      } else if (event.event === 'scan_complete') {
        setScanStates((prev) => ({
          ...prev,
          [event.repo_id]: { status: 'complete', scanId: event.scan_id },
        }));
        if (event.savings_headline) {
          showSuccess(event.savings_headline);
        }
        // Refetch immediately and again after a short delay in case the DB write is still in flight
        refetchRef.current();
        setTimeout(() => refetchRef.current(), 3000);
        setTimeout(() => refetchRef.current(), 8000);
      }
    },
    [showSuccess]
  );

  useWebSocket({ userId: user?.id ?? null, token: accessToken, onMessage: handleWsEvent });

  const handleScan = async (repoId: number) => {
    try {
      setScanStates((prev) => ({ ...prev, [repoId]: { status: 'queued', scanId: null } }));
      const result = await triggerScan({ repo_id: repoId }).unwrap();
      setScanStates((prev) => ({
        ...prev,
        [repoId]: { status: 'queued', scanId: result.scan_id },
      }));
      showSuccess('Scan started — results will update in real time.');
    } catch (err: any) {
      setScanStates((prev) => {
        const next = { ...prev };
        delete next[repoId];
        return next;
      });
      showError(err?.data?.detail ?? 'Failed to start scan. Please try again.');
    }
  };

  const handleTogglePrGate = async (repoId: number, enabled: boolean) => {
    try {
      await updateRepo({ repo_id: repoId, pr_gate_enabled: enabled }).unwrap();
      showSuccess(enabled ? 'PR gate enabled — new PRs will be scanned automatically.' : 'PR gate disabled.');
    } catch (err: any) {
      showError(err?.data?.detail ?? 'Failed to update PR gate setting.');
    }
  };

  const handleConnect = async (repo: AvailableRepository) => {
    try {
      await connectRepo(repo).unwrap();
      showSuccess(`${repo.full_name} connected.`);
    } catch (err: any) {
      showError(err?.data?.detail ?? `Failed to connect ${repo.full_name}.`);
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Header onAdd={() => setAddDialogOpen(true)} onRefresh={refetch} disabled />
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <RepositoryCardSkeleton />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Header onAdd={() => setAddDialogOpen(true)} onRefresh={refetch} disabled={false} />
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load repositories. Please try again.
        </Alert>
      </Box>
    );
  }

  const activeCount = Object.values(scanStates).filter(
    (s) => !['complete', 'failed'].includes(s.status)
  ).length;

  // Active scans with known IDs — each gets its own polling instance
  const activeSyncTargets = Object.entries(scanStates).filter(
    ([, s]) => s.scanId && !['complete', 'failed'].includes(s.status)
  ) as [string, ScanState & { scanId: number }][];

  return (
    <Box>
      {/* Invisible scan pollers — one per in-flight scan */}
      {activeSyncTargets.map(([repoId, state]) => (
        <ScanStatusSync
          key={state.scanId}
          scanId={state.scanId}
          repoId={Number(repoId)}
          onUpdate={handleScanStatusUpdate}
        />
      ))}

      <Header
        onAdd={() => setAddDialogOpen(true)}
        onRefresh={refetch}
        disabled={false}
        activeScans={activeCount}
      />

      {repos && repos.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <GitHub sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No repositories connected yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add a repository to start scanning for technical debt.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddDialogOpen(true)}
            data-onboarding-target="add-repo"
          >
            Add Repository
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ mt: 0.5 }}>
          {(repos ?? []).map((repo) => (
            <Grid item xs={12} sm={6} md={4} key={repo.id}>
              <RepositoryCard
                repository={repo}
                onScan={handleScan}
                scanStatus={scanStates[repo.id]?.status ?? null}
                scanMessage={scanStates[repo.id]?.message}
                latestScanId={scanStates[repo.id]?.scanId ?? repo.latest_scan_id}
                onTogglePrGate={handleTogglePrGate}
                isTogglingPrGate={togglingPrGate}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add repository drawer */}
      <SideDrawer
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        title="Add Repository"
        description="Select a GitHub repository to connect to KodeGauge"
        width={{ xs: '100vw', sm: 520, md: 560 }}
        footer={
          <Button
            onClick={() => setAddDialogOpen(false)}
            variant="contained"
            disableElevation
            sx={{
              fontWeight: 800,
              textTransform: 'none',
              px: 3,
              borderRadius: '10px',
              boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
            }}
          >
            Close
          </Button>
        }
      >
        {availableLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List disablePadding>
            {(availableRepos ?? []).map((repo, i) => {
              const already = connectedNames.has(repo.full_name);
              const unsupported = repo.language ? UNSUPPORTED_LANGUAGES.has(repo.language) : false;
              const sizeMb = Math.round((repo.size_kb ?? 0) / 1024);
              return (
                <React.Fragment key={repo.full_name}>
                  {i > 0 && <Divider component="li" />}
                  <ListItem sx={{ py: 1.5, px: 1 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={700}>
                            {repo.full_name}
                          </Typography>
                          {repo.private && <Lock sx={{ fontSize: 14, color: 'text.disabled' }} />}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                          {repo.language && (
                            <Chip
                              label={repo.language}
                              size="small"
                              color={unsupported ? 'error' : 'default'}
                              icon={unsupported ? <Warning sx={{ fontSize: '14px !important' }} /> : undefined}
                              variant="outlined"
                              sx={{ height: 20, fontSize: 10, fontWeight: 600 }}
                            />
                          )}
                          {sizeMb > 0 && (
                            <Chip
                              label={`${sizeMb} MB`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: 10 }}
                            />
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {already ? (
                        <Chip label="Connected" size="small" color="success" sx={{ fontWeight: 700 }} />
                      ) : unsupported ? (
                        <Chip label="Not supported" size="small" color="error" variant="outlined" sx={{ fontWeight: 600 }} />
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={connecting}
                          onClick={() => handleConnect(repo)}
                          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
                        >
                          Connect
                        </Button>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              );
            })}
            {availableRepos?.length === 0 && (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No repositories available. Make sure the GitHub App is installed on your account.
                </Typography>
              </Box>
            )}
          </List>
        )}
      </SideDrawer>

      <NotificationSnackbar notification={notification} onClose={hideNotification} />
    </Box>
  );
};

// ── Extracted header ──────────────────────────────────────────────────────────
interface HeaderProps {
  onAdd: () => void;
  onRefresh: () => void;
  disabled: boolean;
  activeScans?: number;
}

const Header: React.FC<HeaderProps> = ({ onAdd, onRefresh, disabled, activeScans = 0 }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="h5" fontWeight={700}>
          Repositories
        </Typography>
        {activeScans > 0 && (
          <Chip
            icon={<CircularProgress size={11} sx={{ color: 'inherit !important' }} />}
            label={`${activeScans} scan${activeScans > 1 ? 's' : ''} running`}
            color="info"
            size="small"
            variant="outlined"
          />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary">
        Connect repos and trigger AI-powered debt scans
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button variant="outlined" startIcon={<Refresh />} onClick={onRefresh} disabled={disabled}>
        Refresh
      </Button>
      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={onAdd}
        disabled={disabled}
        data-onboarding-target="add-repo"
      >
        Add Repo
      </Button>
    </Box>
  </Box>
);
